import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Grid, Paper } from '@mui/material';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { IUserDataToken } from 'utils/types';
import { S3Uploader } from 'utils/S3Uploader';
import { IAditionalFields, IRecordHeaders } from 'ui-component/records/types';
import { List, UploadBanner } from './components';
import { AttachmentRow } from './types';
import { IFormatDataValues } from '../types';
import { useAccessAttachments } from './hooks/useAccessAttachments';
import { isJsonString } from 'utils/stringHelpers';
import { useDocumentViewer } from 'views/DocumentViewer/hooks/useDocumentViewer';
import { useQuery } from '@apollo/client';
import { QUERY_ME } from 'graphql/mutations/auth';

export type AttachmentsProps = {
    fullHeight?: boolean;
    attachmentFieldIds?: number[] | undefined;
    attachmentsData?: IAditionalFields[] | undefined;
    recordTypeId?: string;
    roleId?: number | null;
    onUpdateAttachment: (payload: IFormatDataValues) => Promise<IRecordHeaders | boolean>;
};

export const Attachments = ({
    fullHeight,
    attachmentFieldIds,
    attachmentsData,
    onUpdateAttachment,
    recordTypeId,
    roleId
}: AttachmentsProps) => {
    const { data: curUserData } = useQuery(QUERY_ME);
    const userData: IUserDataToken = curUserData?.me ? JSON.parse(curUserData.me) : null;

    const dispatch = useDispatch();
    const [rows, setRows] = useState<AttachmentRow[]>([]);
    const { handleSetAttachmentUpdated } = useDocumentViewer();
    const { fieldToUpload, fieldAccess } = useAccessAttachments(Number(recordTypeId), attachmentFieldIds || [], Number(roleId));
    const [uploadingFile, setUploadingFile] = useState<boolean>(false);

    const handleSelectFile = async (file: File) => {
        try {
            setUploadingFile(true);
            const id = uuidv4();
            const key = await S3Uploader(file, id);

            const newRow = {
                id,
                name: file.name,
                description: '',
                fileType: file.type || `file/${file.name.split('.')?.slice(-1)?.[0] || ''}`,
                fileSize: (file.size / 1024).toFixed(2),
                createdAt: new Date().toISOString(),
                createdBy: userData.userId,
                fileUrl: key,
                fieldId: fieldToUpload?.fieldId
            };

            const newValue = [...rows.filter(({ fieldId }) => fieldId === fieldToUpload?.fieldId), newRow];
            const attachmentRow = attachmentsData?.find(({ fieldId }) => fieldId === fieldToUpload?.fieldId);
            const payload = attachmentRow
                ? {
                      tag: attachmentRow.tag,
                      value: JSON.stringify(newValue)
                  }
                : {
                      tag: `;;${attachmentFieldIds?.find((fieldId) => fieldId === fieldToUpload?.fieldId)}`,
                      value: JSON.stringify(newValue),
                      objectValuesByProperty: undefined
                  };

            const res = await onUpdateAttachment({ baseData: {}, additionalFields: [payload] } as IFormatDataValues);

            if (res) {
                // no need to manually update the state here because the parent handler will auto update the cache/state after performing the API call which will trigger a re-render and then the state updating logic in the useEffect will execute
                dispatch(
                    openSnackbar({
                        open: true,
                        type: 'success',
                        title: 'Success',
                        subtitle: 'File Uploaded'
                    })
                );
                handleSetAttachmentUpdated();
            } else throw new Error('Save attachment failed');
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Save attachment failed',
                    subtitle: (err as Error).message || 'Internal server error.'
                })
            );
        } finally {
            setUploadingFile(false);
        }
    };

    const handleUpdateRow = async (newRow: AttachmentRow) => {
        if (!attachmentsData) return;
        const newValue = rows.filter(({ fieldId }) => fieldId === newRow.fieldId).map((row) => (row.id === newRow.id ? newRow : row));
        const payload = { tag: attachmentsData.find(({ fieldId }) => fieldId === newRow.fieldId)?.tag, value: JSON.stringify(newValue) };

        const res = await onUpdateAttachment({ baseData: {}, additionalFields: [payload] } as IFormatDataValues);

        if (res) {
            setRows(newValue);

            dispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Success',
                    subtitle: 'File Updated'
                })
            );
            handleSetAttachmentUpdated();
        } else
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Error',
                    subtitle: 'Update file failed'
                })
            );
    };

    const handleDeleteRow = async (row: AttachmentRow) => {
        if (!attachmentsData) return;
        const newValue = rows.filter(({ fieldId }) => fieldId === row.fieldId).filter((r) => r.id !== row.id);
        const payload = { tag: attachmentsData.find(({ fieldId }) => fieldId === row.fieldId)?.tag, value: JSON.stringify(newValue) };

        const res = await onUpdateAttachment({ baseData: {}, additionalFields: [payload] } as IFormatDataValues);

        if (res) {
            setRows(newValue);

            dispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Success',
                    subtitle: 'File Deleted'
                })
            );
            handleSetAttachmentUpdated();
        } else
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Error',
                    subtitle: 'Delete file failed'
                })
            );
    };

    useEffect(() => {
        if (attachmentsData && fieldAccess) {
            setRows(
                attachmentsData
                    .filter(
                        (data) =>
                            fieldAccess?.find((val) => val.fieldId === data.fieldId)?.access.toLocaleLowerCase() !== 'notallowed' &&
                            isJsonString(data.value)
                    )
                    .map((data) => {
                        const field = fieldAccess?.find((val) => val.fieldId === data.fieldId);
                        return JSON.parse(data.value).map((val: any) => ({
                            ...val,
                            access: field?.access.toLocaleLowerCase(),
                            recordFieldName: field?.recordFieldName,
                            fieldId: data.fieldId
                        }));
                    })
                    .flat()
            );
        }
    }, [attachmentsData, fieldAccess]);

    return (
        <Paper id="record-attachment-section" style={{ height: 'calc(100% - 60px)' }}>
            {fieldToUpload && <UploadBanner onFileChange={handleSelectFile} disabled={uploadingFile} hasRows={rows.length > 0} />}
            {!!rows.length && (
                <Grid container justifyContent="center" alignItems="center" sx={{ height: 'calc(100% - 94px)' }}>
                    <Grid item xs={12} height="100%">
                        <List rows={rows} onDeleteRow={handleDeleteRow} onUpdateRow={handleUpdateRow} density="compact" />
                    </Grid>
                </Grid>
            )}
        </Paper>
    );
};
